import { Check, CircleSlash, PackagePlus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { StockArticle } from "@/lib/stock.api";
import {
  getSellingPrice,
  getSupplierPrice,
  isLowStock,
} from "../stock-calculations";
import { formatMoney } from "../stock-formatters";
import { MobileFact } from "./StockDetailPrimitives";
import { StockArticleStatusBadge } from "./StockBadges";

interface StockArticleGridProps {
  articles: StockArticle[];
  canManage: boolean;
  toggling: boolean;
  onSelect: (article: StockArticle) => void;
  onToggleActive: (article: StockArticle) => void;
  onEdit: (article: StockArticle) => void;
  onRestock: (article: StockArticle) => void;
}

export function StockArticleGrid({
  articles,
  canManage,
  toggling,
  onSelect,
  onToggleActive,
  onEdit,
  onRestock,
}: StockArticleGridProps) {
  if (articles.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-8 text-center text-[12px] text-muted-foreground">
        Aucun article.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3">
      {articles.map((article) => {
        const lowStock = isLowStock(article);

        return (
          <div key={article.id} className="rounded-lg border border-border bg-card p-3">
            <div className="flex items-start justify-between gap-3">
              <button type="button" className="min-w-0 text-left" onClick={() => onSelect(article)}>
                <p className="truncate text-[13px] font-semibold text-foreground">
                  {article.name}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Article #{article.id} - unité {article.unit}
                </p>
              </button>
              <StockArticleStatusBadge article={article} />
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-border pt-3 text-[11px]">
              <MobileFact
                label="En stock"
                value={`${article.onHand} ${article.unit}`}
                danger={lowStock}
              />
              <MobileFact label="Seuil bas" value={`${article.minLevel} ${article.unit}`} />
              <MobileFact label="Fournisseur" value={formatMoney(getSupplierPrice(article))} />
              <MobileFact label="Vente" value={formatMoney(getSellingPrice(article))} />
            </div>

            {canManage && (
              <div className="mt-3 flex items-center justify-end gap-2">
                <Button variant="ghost" size="icon" onClick={() => onEdit(article)} title="Modifier">
                  <Pencil size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRestock(article);
                  }}
                  title="Réapprovisionner"
                >
                  <PackagePlus size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onToggleActive(article)}
                  title={article.active ? "Désactiver" : "Activer"}
                  disabled={toggling}
                >
                  {article.active ? <CircleSlash size={14} /> : <Check size={14} />}
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
